import { useId, useState, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  Clock3,
  ExternalLink,
  Info,
  Minus,
} from "lucide-react";

export type GrowthSeriesPoint = { at: string; value: number };

export type GrowthDeltaPolarity = "higher-better" | "lower-better";

export type GrowthFreshness = "live" | "quiet" | "stale";

export type GrowthTickerItem = {
  id: string;
  source: string;
  label: string;
  value: string;
  delta?: number | null;
  polarity?: GrowthDeltaPolarity;
  /** Shown when delta is missing — default "watching". */
  watchLabel?: string;
};

export type GrowthToolLink = {
  label: string;
  href: string;
};

export type GrowthMetricTile = {
  id: string;
  label: string;
  value: string;
  delta?: number | null;
  /** Prefer absolute delta chip; optional % label like Buffer Insights. */
  pctLabel?: string | null;
  polarity?: GrowthDeltaPolarity;
  band?: "red" | "yellow" | "green" | null;
  tip?: string;
  /** When set, the tile value (or whole tile) opens this URL. */
  href?: string;
};

export type GrowthWindowOption = {
  id: string;
  label: string;
};

export type GrowthIndexRail = {
  notIndexedValue: string;
  notIndexedHref: string;
  notIndexedSub?: string | null;
  indexedValue: string;
  indexedHref: string;
  lastIndexedLabel: string;
  reasonsHref: string;
  reasonsLabel?: string;
  tip?: string;
};

export type GrowthSignalChapterProps = {
  title: string;
  eyebrow: string;
  tag: string;
  hero: {
    value: string;
    label: string;
    delta?: string | number | null;
    /** Optional percent change label e.g. "+18%" */
    pctLabel?: string | null;
    comparisonLabel?: string | null;
    /** Absolute health band (e.g. Buffer queue refill). */
    band?: "red" | "yellow" | "green" | null;
  };
  series?: GrowthSeriesPoint[];
  supports: Array<{
    label: string;
    value: string;
    delta?: number | null;
    polarity?: GrowthDeltaPolarity;
  }>;
  /** Insights-style metric cards shown in the compact summary (e.g. top 4). */
  compactTiles?: GrowthMetricTile[];
  /** Extra metric cards shown when expanded. */
  expandedTiles?: GrowthMetricTile[];
  /** Time-range chips next to the title. */
  windows?: GrowthWindowOption[];
  windowId?: string;
  onWindowChange?: (id: string) => void;
  /** Bottom holdover / compare strip (always visible in compact). */
  holdover?: {
    label?: string;
    tip?: string;
    detail?: string;
    /** Tooltip-only label (e.g. last crawl) shown via an icon. */
    iconTip?: string | null;
    /** Compact outbound link (e.g. not-indexed count). */
    link?: { href: string; label: string } | null;
  } | null;
  /** Search Console-style indexing status strip. */
  indexRail?: GrowthIndexRail | null;
  /** Quiet footer under the tool link (e.g. last sync). */
  footerNote?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  unmatched?: boolean;
  unmatchedMessage?: ReactNode;
  freshness?: GrowthFreshness;
  /** How to color the hero delta. Default: higher is good (pine). */
  polarity?: GrowthDeltaPolarity;
  /** Open provider dashboards in a new tab. */
  toolLinks?: GrowthToolLink[];
  /** Start expanded (rare). */
  defaultOpen?: boolean;
  /** Optional GA-style breakdown tables shown when expanded. */
  breakdowns?: Array<{
    title: string;
    dimensionLabel: string;
    metricLabel: string;
    rows: Array<{
      name: string;
      value: string;
      delta?: number | null;
      pctLabel?: string | null;
    }>;
  }>;
};

const EMBER = "#D8754F";
const GOLDEN = "#E8AE50";
const PINE = "#426b50";

function formatPointLabel(at: string): string {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return at.slice(0, 10);
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Span change across the series (honest trend, not invented). */
export function seriesSpanDelta(
  series?: GrowthSeriesPoint[] | null,
): number | null {
  if (!series || series.length < 2) return null;
  const first = series[0]?.value;
  const last = series[series.length - 1]?.value;
  if (first == null || last == null) return null;
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  return last - first;
}

/** Last step change — the “ticker tick”. */
export function seriesTickDelta(
  series?: GrowthSeriesPoint[] | null,
): number | null {
  if (!series || series.length < 2) return null;
  const a = series[series.length - 2]?.value;
  const b = series[series.length - 1]?.value;
  if (a == null || b == null) return null;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return b - a;
}

export function parseDeltaNumber(
  delta: string | number | null | undefined,
): number | null {
  if (delta == null || delta === "") return null;
  if (typeof delta === "number") {
    return Number.isFinite(delta) ? delta : null;
  }
  const cleaned = delta.replace(/[^0-9+\-.]/g, "");
  if (!cleaned || cleaned === "+" || cleaned === "-") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function formatSignedDelta(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  if (n === 0) return "0";
  const rounded = Math.abs(n) >= 10 ? Math.round(n) : Math.round(n * 10) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded}`;
}

export function formatPctDelta(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  const rounded = Math.abs(n) >= 10 ? Math.round(n) : Math.round(n * 10) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
}

export function pctFromPrior(
  current: number | null | undefined,
  prior: number | null | undefined,
): number | null {
  if (current == null || prior == null) return null;
  if (!Number.isFinite(current) || !Number.isFinite(prior)) return null;
  if (prior === 0) return current === 0 ? 0 : null;
  return ((current - prior) / prior) * 100;
}

export function deltaTone(
  n: number | null | undefined,
  polarity: GrowthDeltaPolarity = "higher-better",
): "up" | "down" | "flat" {
  if (n == null || !Number.isFinite(n) || n === 0) return "flat";
  const higherIsGood = polarity === "higher-better";
  if (n > 0) return higherIsGood ? "up" : "down";
  return higherIsGood ? "down" : "up";
}

function DeltaChip({
  delta,
  polarity = "higher-better",
  size = "md",
}: {
  delta: number | null | undefined;
  polarity?: GrowthDeltaPolarity;
  size?: "sm" | "md";
}) {
  const label = formatSignedDelta(delta);
  if (label == null) return null;
  const tone = deltaTone(delta, polarity);
  const Icon =
    tone === "up" ? ArrowUpRight : tone === "down" ? ArrowDownRight : Minus;
  return (
    <span className={`op-signal-delta is-${tone} is-${size}`}>
      <Icon size={size === "sm" ? 12 : 14} aria-hidden />
      {label}
    </span>
  );
}

function ToolLinks({ links }: { links: GrowthToolLink[] }) {
  if (!links.length) return null;
  return (
    <div className="op-signal-tools">
      {links.map((link) => (
        <a
          key={link.href + link.label}
          className="op-signal-tool-link"
          href={link.href}
          target="_blank"
          rel="noreferrer"
        >
          {link.label}
          <ExternalLink size={12} aria-hidden />
        </a>
      ))}
    </div>
  );
}

function MetricTileCard({
  tile,
  polarity,
}: {
  tile: GrowthMetricTile;
  polarity: GrowthDeltaPolarity;
}) {
  const tilePolarity = tile.polarity ?? polarity;
  const body = (
    <>
      <div className="op-signal-tile-top">
        <small>{tile.label}</small>
        {tile.tip ? (
          <span className="op-signal-tip" title={tile.tip} aria-label={tile.tip}>
            <Info size={12} strokeWidth={2} aria-hidden />
          </span>
        ) : null}
      </div>
      <div className="op-signal-tile-row">
        <strong
          className={tile.band ? `is-band-${tile.band}` : undefined}
        >
          {tile.value}
        </strong>
        {tile.pctLabel ? (
          <span
            className={`op-signal-tile-pct is-${deltaTone(tile.delta, tilePolarity)}`}
          >
            {tile.delta != null && tile.delta !== 0 ? (
              tile.delta > 0 && tilePolarity === "higher-better" ? (
                <ArrowUpRight size={12} aria-hidden />
              ) : tile.delta < 0 && tilePolarity === "higher-better" ? (
                <ArrowDownRight size={12} aria-hidden />
              ) : tile.delta > 0 ? (
                <ArrowDownRight size={12} aria-hidden />
              ) : (
                <ArrowUpRight size={12} aria-hidden />
              )
            ) : null}
            {tile.pctLabel}
          </span>
        ) : (
          <DeltaChip
            delta={tile.delta}
            polarity={tilePolarity}
            size="sm"
          />
        )}
      </div>
    </>
  );

  if (tile.href) {
    return (
      <a
        className={`op-signal-tile is-link${tile.band ? ` is-band-${tile.band}` : ""}`}
        href={tile.href}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {body}
      </a>
    );
  }

  return (
    <div
      className={`op-signal-tile${tile.band ? ` is-band-${tile.band}` : ""}`}
    >
      {body}
    </div>
  );
}

function MiniSpark({
  data,
  stroke,
  fillId,
}: {
  data: Array<{ at: string; value: number; label: string }>;
  stroke: string;
  fillId: string;
}) {
  return (
    <div className="op-signal-spark-mini" aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
              <stop offset="100%" stopColor={GOLDEN} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <YAxis domain={["dataMin", "dataMax"]} hide />
          <Area
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={1.75}
            fill={`url(#${fillId})`}
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function GrowthTicker({ items }: { items: GrowthTickerItem[] }) {
  if (!items.length) return null;
  const loop = [...items, ...items];
  return (
    <div className="op-growth-ticker" aria-label="Live growth signals">
      <div className="op-growth-ticker-rail">
        <div className="op-growth-ticker-track">
          {loop.map((item, i) => {
            const tone = deltaTone(item.delta, item.polarity);
            return (
              <span
                key={`${item.id}-${i}`}
                className={`op-growth-ticker-item is-${tone}`}
              >
                <em>{item.source}</em>
                <strong>{item.label}</strong>
                <b>{item.value}</b>
                {item.delta != null ? (
                  <DeltaChip
                    delta={item.delta}
                    polarity={item.polarity}
                    size="sm"
                  />
                ) : (
                  <span className="op-growth-ticker-watch">
                    {item.watchLabel ?? "watching"}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact growth chapter: summary row by default, expand for detail + tool links.
 */
export function GrowthSignalChapter({
  title,
  eyebrow,
  tag,
  hero,
  series,
  supports,
  compactTiles,
  expandedTiles,
  windows,
  windowId,
  onWindowChange,
  holdover,
  indexRail,
  footerNote,
  note,
  actions,
  unmatched,
  unmatchedMessage,
  freshness = "quiet",
  polarity = "higher-better",
  toolLinks = [],
  defaultOpen = false,
  breakdowns,
}: GrowthSignalChapterProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const hasTiles = Boolean(compactTiles && compactTiles.length > 0);

  const chartData =
    series && series.length >= 2
      ? series.map((p) => ({
          at: p.at,
          value: p.value,
          label: formatPointLabel(p.at),
        }))
      : null;

  const explicitDelta = parseDeltaNumber(hero.delta);
  const spanDelta = seriesSpanDelta(series);
  const tickDelta = seriesTickDelta(series);
  const heroDelta = explicitDelta ?? spanDelta;
  const tone = deltaTone(heroDelta, polarity);
  const stroke = tone === "up" ? PINE : tone === "down" ? EMBER : EMBER;
  const fillId = `opSparkFill-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const primaryTool = toolLinks[0] ?? null;

  return (
    <section
      className={`op-panel op-signal-chapter is-compact is-gallery is-${freshness}${
        open ? " is-open" : ""
      }${tone !== "flat" ? ` is-trend-${tone}` : ""}${
        hasTiles ? " is-tiled" : ""
      }`}
    >
      <div className="op-signal-live-edge" aria-hidden />

      <div className="op-signal-summary">
        <div className="op-signal-summary-top">
          <span className="op-signal-summary-title">
            <strong>{title}</strong>
            {windows && windows.length > 0 ? (
              <span
                className={`op-signal-windows${onWindowChange ? "" : " is-readonly"}`}
                role="group"
                aria-label="Monitoring window"
              >
                {windows.map((w) =>
                  onWindowChange ? (
                    <button
                      key={w.id}
                      type="button"
                      className="op-signal-window"
                      aria-pressed={windowId === w.id}
                      onClick={() => onWindowChange(w.id)}
                    >
                      {w.label}
                    </button>
                  ) : (
                    <span
                      key={w.id}
                      className={`op-signal-window${
                        windowId === w.id ? " is-active" : ""
                      }`}
                      aria-current={windowId === w.id ? "true" : undefined}
                    >
                      {w.label}
                    </span>
                  ),
                )}
              </span>
            ) : null}
            {freshness === "live" ? (
              <span className="op-signal-pulse" title="Recently synced">
                <i /> Live
              </span>
            ) : freshness === "stale" ? (
              <span className="op-tag op-signal-stale-tag">Stale</span>
            ) : null}
          </span>
          <button
            type="button"
            className="op-signal-expand"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronDown
              size={16}
              className="op-signal-chevron"
              aria-hidden
            />
          </button>
        </div>
        <button
          type="button"
          className="op-signal-summary-main"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          <small className="op-signal-summary-eye">{eyebrow}</small>
          {!unmatched && hasTiles ? (
            <div className="op-signal-tile-grid">
              {compactTiles!.map((tile) => (
                <MetricTileCard
                  key={tile.id}
                  tile={tile}
                  polarity={polarity}
                />
              ))}
            </div>
          ) : !unmatched ? (
            <span className="op-signal-summary-metrics">
              <span className="op-signal-summary-hero">
                <b
                  key={hero.value}
                  className={`op-signal-tick-in${
                    hero.band ? ` is-band-${hero.band}` : ""
                  }`}
                >
                  {hero.value}
                </b>
                <em>{hero.label}</em>
                <DeltaChip delta={heroDelta} polarity={polarity} size="sm" />
                {hero.pctLabel ? (
                  <span className="op-signal-pct">{hero.pctLabel}</span>
                ) : null}
                {hero.comparisonLabel ? (
                  <span className="op-signal-vs">{hero.comparisonLabel}</span>
                ) : null}
              </span>
              {chartData ? (
                <MiniSpark data={chartData} stroke={stroke} fillId={fillId} />
              ) : (
                <span className="op-signal-spark-placeholder" aria-hidden />
              )}
            </span>
          ) : (
            <span className="op-signal-summary-gap">Needs mapping</span>
          )}
        </button>
        {indexRail && !unmatched ? (
          <div className="op-signal-index-rail">
            <div className="op-signal-index-pair">
              <a
                className="op-signal-index-card is-excluded"
                href={indexRail.notIndexedHref}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="op-signal-index-label">Not indexed</span>
                <strong>{indexRail.notIndexedValue}</strong>
                {indexRail.notIndexedSub ? (
                  <em>{indexRail.notIndexedSub}</em>
                ) : null}
              </a>
              <a
                className="op-signal-index-card is-indexed"
                href={indexRail.indexedHref}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="op-signal-index-label">Indexed</span>
                <strong>{indexRail.indexedValue}</strong>
              </a>
            </div>
            <div className="op-signal-index-meta">
              <span>{indexRail.lastIndexedLabel}</span>
              {indexRail.tip ? (
                <span
                  className="op-signal-tip"
                  title={indexRail.tip}
                  aria-label={indexRail.tip}
                >
                  <Info size={12} strokeWidth={2} aria-hidden />
                </span>
              ) : null}
              <a
                className="op-signal-index-reasons"
                href={indexRail.reasonsHref}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {indexRail.reasonsLabel ?? "Reasons why it’s not indexed"}
                <ExternalLink size={11} aria-hidden />
              </a>
            </div>
          </div>
        ) : null}
        {holdover && !unmatched ? (
          <div className="op-signal-holdover">
            <span className="op-signal-holdover-main">
              {holdover.iconTip ? (
                <span
                  className="op-signal-holdover-icon"
                  title={holdover.iconTip}
                  aria-label={holdover.iconTip}
                >
                  <Clock3 size={13} strokeWidth={2} aria-hidden />
                </span>
              ) : null}
              {holdover.label ? <span>{holdover.label}</span> : null}
              {holdover.tip && !holdover.iconTip ? (
                <span
                  className="op-signal-tip"
                  title={holdover.tip}
                  aria-label={holdover.tip}
                >
                  <Info size={12} strokeWidth={2} aria-hidden />
                </span>
              ) : null}
            </span>
            {holdover.link ? (
              <a
                className="op-signal-holdover-link"
                href={holdover.link.href}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {holdover.link.label}
                <ExternalLink size={11} aria-hidden />
              </a>
            ) : holdover.detail ? (
              <span className="op-signal-holdover-detail">{holdover.detail}</span>
            ) : null}
          </div>
        ) : null}
        {primaryTool ? (
          <a
            className="op-signal-open-tool"
            href={primaryTool.href}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={13} aria-hidden />
            <span>{primaryTool.label.replace(/^Open\s+/i, "")}</span>
          </a>
        ) : null}
        {footerNote ? (
          <p className="op-signal-footer-note">{footerNote}</p>
        ) : null}
      </div>

      {open ? (
        <div id={panelId} className="op-signal-detail">
          <div className="op-signal-detail-head">
            <span className="op-tag">{tag}</span>
            <ToolLinks links={toolLinks} />
          </div>

          {unmatched ? (
            <p className="op-growth-buffer-note">{unmatchedMessage}</p>
          ) : hasTiles ? (
            expandedTiles && expandedTiles.length > 0 ? (
              <div className="op-signal-tile-grid is-expanded" role="group">
                {expandedTiles.map((tile) => (
                  <MetricTileCard
                    key={tile.id}
                    tile={tile}
                    polarity={polarity}
                  />
                ))}
              </div>
            ) : null
          ) : (
            <>
              <div className="op-signal-hero-row">
                <div className="op-signal-hero">
                  <strong
                    key={`d-${hero.value}`}
                    className={`op-signal-hero-value op-signal-tick-in${
                      hero.band ? ` is-band-${hero.band}` : ""
                    }`}
                  >
                    {hero.value}
                  </strong>
                  <span className="op-signal-hero-label">{hero.label}</span>
                  <div className="op-signal-hero-meta">
                    <DeltaChip delta={heroDelta} polarity={polarity} />
                    {tickDelta != null &&
                    heroDelta != null &&
                    tickDelta !== heroDelta ? (
                      <span className="op-signal-tick-label">
                        last step{" "}
                        <DeltaChip
                          delta={tickDelta}
                          polarity={polarity}
                          size="sm"
                        />
                      </span>
                    ) : null}
                  </div>
                </div>
                {chartData ? (
                  <div className="op-signal-spark" aria-hidden>
                    <ResponsiveContainer width="100%" height={72}>
                      <AreaChart
                        data={chartData}
                        margin={{ top: 6, right: 4, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient
                            id={`${fillId}-detail`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={stroke}
                              stopOpacity={0.38}
                            />
                            <stop
                              offset="100%"
                              stopColor={GOLDEN}
                              stopOpacity={0.02}
                            />
                          </linearGradient>
                        </defs>
                        <YAxis domain={["dataMin", "dataMax"]} hide />
                        <Tooltip
                          contentStyle={{
                            background: "#fffaf5",
                            border: "1px solid #e9ded4",
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          labelFormatter={(_, payload) =>
                            payload?.[0]?.payload?.label ?? ""
                          }
                          formatter={(value: number | string) => [
                            value,
                            hero.label,
                          ]}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke={stroke}
                          strokeWidth={2.25}
                          fill={`url(#${fillId}-detail)`}
                          isAnimationActive
                          animationDuration={650}
                          dot={false}
                          activeDot={{ r: 3.5, fill: stroke }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="op-signal-spark-empty" aria-hidden>
                    <span className="op-signal-waiting-dot" />
                    <span>Waiting for history</span>
                  </div>
                )}
              </div>

              {supports.length > 0 ? (
                <div className="op-signal-supports" role="group">
                  {supports.map((s) => (
                    <div key={s.label} className="op-signal-support">
                      <small>{s.label}</small>
                      <div className="op-signal-support-row">
                        <strong>{s.value}</strong>
                        <DeltaChip
                          delta={s.delta}
                          polarity={s.polarity ?? polarity}
                          size="sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}

          {note ? <p className="op-growth-buffer-note">{note}</p> : null}
          {breakdowns && breakdowns.length > 0 ? (
            <div className="op-signal-breakdowns">
              {breakdowns.map((table) => (
                <div key={table.title} className="op-signal-breakdown">
                  <div className="op-signal-breakdown-head">
                    <strong>{table.title}</strong>
                  </div>
                  {table.rows.length === 0 ? (
                    <p className="op-growth-buffer-note">No rows in this window.</p>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>{table.dimensionLabel}</th>
                          <th>{table.metricLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.rows.map((row) => (
                          <tr key={`${table.title}-${row.name}`}>
                            <td title={row.name}>{row.name}</td>
                            <td>
                              <span className="op-signal-breakdown-val">
                                {row.value}
                              </span>
                              {row.pctLabel ? (
                                <span
                                  className={`op-signal-tile-pct is-${
                                    (row.delta ?? 0) > 0
                                      ? "up"
                                      : (row.delta ?? 0) < 0
                                        ? "down"
                                        : "flat"
                                  }`}
                                >
                                  {row.pctLabel}
                                </span>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          {actions ? <div className="op-signal-actions">{actions}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
